import { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { getTestCatalogItems, searchTestCatalog } from '../../../services/testCatalogService';
import type { TestCatalogItem } from '../../../types/testCatalogItem';

interface CataloguePickerModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (catalogItemKeys: string[]) => void;
}

export const CataloguePickerModal = ({ open, onClose, onAdd }: CataloguePickerModalProps) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<TestCatalogItem[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedKeys(new Set());
    setSearchQuery('');
    fetchItems('');
  }, [open]);

  const fetchItems = async (query: string) => {
    setLoading(true);
    try {
      const data = query.trim()
        ? await searchTestCatalog(query)
        : await getTestCatalogItems();
      setItems(data.filter((i) => i.IsActive));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    fetchItems(value);
  };

  const toggleItem = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAdd = () => {
    onAdd(Array.from(selectedKeys));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>Add from Test Catalogue</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          placeholder="Search tests..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={items.length > 0 && selectedKeys.size === items.length}
                    indeterminate={selectedKeys.size > 0 && selectedKeys.size < items.length}
                    onChange={() => {
                      if (selectedKeys.size === items.length) {
                        setSelectedKeys(new Set());
                      } else {
                        setSelectedKeys(new Set(items.map((i) => i.TestCatalogItemKey)));
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Test Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>ASTM</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price (CAD)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">Loading...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No catalogue items found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((item) => {
                const isSelected = selectedKeys.has(item.TestCatalogItemKey);
                return (
                  <TableRow
                    key={item.TestCatalogItemKey}
                    hover
                    onClick={() => toggleItem(item.TestCatalogItemKey)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.06) : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={isSelected} />
                    </TableCell>
                    <TableCell>{item.TestName}</TableCell>
                    <TableCell>{item.AstmCode ?? ''}</TableCell>
                    <TableCell>{item.Category ?? ''}</TableCell>
                    <TableCell align="right">
                      {item.UnitPriceCad.toLocaleString('en-CA', {
                        style: 'currency',
                        currency: 'CAD',
                      })}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={selectedKeys.size === 0}
          sx={{ textTransform: 'none' }}
        >
          Add Selected ({selectedKeys.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
